/**
 * Bullet - Projectile fired by soldiers
 *
 * Features:
 * - Small circle with trailing effect
 * - Travels upward toward enemies
 * - Supports object pooling for performance
 * - Destroys on collision or leaving screen
 */

import Phaser from 'phaser';
import { GAME_WIDTH } from '../config';
import { WeaponType, WEAPONS } from '../utils/constants';

export interface BulletConfig {
  x: number;
  y: number;
  targetX: number;
  targetY: number;
  weaponType: WeaponType;
  /** Logical bullets may stay hidden when a large army would flood the screen. */
  renderVisible?: boolean;
}

export class Bullet extends Phaser.GameObjects.Container {
  /** Bullet scale based on weapon type */
  private static readonly SCALES: Record<WeaponType, number> = {
    [WeaponType.Pistol]: 1,
    [WeaponType.Rifle]: 1,
    [WeaponType.MachineGun]: 0.8,
    [WeaponType.Rocket]: 1,
  };

  /** Bullet speed in pixels per second */
  private static readonly SPEED = 400;

  /** Bullet sprite */
  private bulletSprite: Phaser.GameObjects.Image;

  /** Velocity components */
  private vx: number = 0;
  private vy: number = 0;

  /** Weapon type determines damage */
  private weaponType: WeaponType = WeaponType.Pistol;

  /** Bullet size */
  private size: number = 4;

  /** Whether bullet is active */
  private isActive: boolean = false;

  /** Whether this bullet is managed by a pool */
  private isPooled: boolean = false;


  constructor(scene: Phaser.Scene, config?: BulletConfig) {
    super(scene, config?.x ?? 0, config?.y ?? 0);

    // Create bullet sprite
    this.bulletSprite = scene.add.image(0, 0, 'bullet-friendly');
    this.add(this.bulletSprite);

    // Initialize if config provided (non-pooled creation)
    if (config) {
      this.reset(config);
      scene.add.existing(this);
    } else {
      // Pooled bullet - start hidden
      this.setVisible(false);
      this.isActive = false;
    }
  }

  /**
   * Resets bullet state for reuse (pooling support)
   */
  reset(config: BulletConfig): void {
    this.x = config.x;
    this.y = config.y;
    this.weaponType = config.weaponType;
    const scale = Bullet.SCALES[config.weaponType];
    const spriteKey = config.weaponType === WeaponType.Rocket ? 'rocket' : 'bullet-friendly';
    this.bulletSprite.setTexture(spriteKey);
    this.bulletSprite.setScale(scale);
    this.bulletSprite.setVisible(config.renderVisible ?? true);
    this.size = this.bulletSprite.displayWidth / 2;

    // Calculate direction to target
    const dx = config.targetX - config.x;
    const dy = config.targetY - config.y;
    const distance = Math.sqrt(dx * dx + dy * dy);

    if (distance > 0) {
      this.vx = (dx / distance) * Bullet.SPEED;
      this.vy = (dy / distance) * Bullet.SPEED;
    } else {
      // Default to shooting straight up
      this.vx = 0;
      this.vy = -Bullet.SPEED;
    }

    // Align the tracer with its flight path so shots read as deliberate streaks.
    this.bulletSprite.setRotation(Math.atan2(this.vy, this.vx) + Math.PI / 2);

    // Activate
    this.isActive = true;
    this.setVisible(true);
    this.setAlpha(1);
  }

  /**
   * Marks this bullet as managed by a pool
   */
  setPooled(pooled: boolean): void {
    this.isPooled = pooled;
  }

  /**
   * Returns whether this bullet is pooled
   */
  getIsPooled(): boolean {
    return this.isPooled;
  }


  /**
   * Updates bullet position each frame
   */
  update(_time: number, delta: number): void {
    if (!this.isActive) return;

    // Move bullet
    const dt = delta / 1000;
    this.x += this.vx * dt;
    this.y += this.vy * dt;

    // Check if off-screen
    if (this.isOffScreen()) {
      this.deactivate();
    }
  }

  /**
   * Checks if bullet is outside game area
   */
  private isOffScreen(): boolean {
    const margin = this.size * 2;
    return (
      this.x < -margin ||
      this.x > GAME_WIDTH + margin ||
      this.y < -margin ||
      this.y > 900 // Below screen
    );
  }

  /**
   * Gets the damage this bullet deals
   */
  getDamage(): number {
    return WEAPONS[this.weaponType].damage;
  }

  /**
   * Gets the weapon type
   */
  getWeaponType(): WeaponType {
    return this.weaponType;
  }

  /**
   * Checks if this is an AOE weapon
   */
  isAOE(): boolean {
    return WEAPONS[this.weaponType].isAOE;
  }

  /**
   * Gets number of targets for AOE
   */
  getAOETargets(): number {
    return WEAPONS[this.weaponType].aoeTargets ?? 1;
  }

  /**
   * Returns whether bullet is active
   */
  getIsActive(): boolean {
    return this.isActive;
  }

  /**
   * Gets collision radius
   */
  getCollisionRadius(): number {
    return this.size;
  }

  /**
   * Deactivates the bullet (hit something or went off-screen)
   * For pooled bullets, hides instead of destroying
   */
  deactivate(): void {
    if (!this.isActive) return;
    this.isActive = false;

    if (this.isPooled) {
      // Just hide - pool will reclaim
      this.setVisible(false);
    } else {
      // Not pooled - destroy normally
      this.destroy();
    }
  }

  /**
   * Cleanup
   */
  destroy(fromScene?: boolean): void {
    this.isActive = false;
    super.destroy(fromScene);
  }
}
